import type { ComponentContentSchema } from '@vibesites/runtime';
import { RichTextContent } from '@vibesites/runtime/react';
import { useScrollReveal } from '../hooks/useScrollReveal';
import { useRevealStyles } from '../hooks/useRevealStyles';
import { useColor4 } from '../hooks/useColor4';

/**
 * Opening intro block for the Better-Built Living page.
 * Centered title + paragraph that frames the "Better-Built Living" concept
 * before the benefits grid.
 */

export function BetterBuiltIntro() {
  useRevealStyles();
  const sectionRef = useScrollReveal<HTMLDivElement>();
  const color4 = useColor4();

  return (
    <section className="w-full pt-14 pb-6 md:pt-20 md:pb-8 bg-[var(--vibe-color-background)]">
      <div ref={sectionRef} className="reveal-fade-up max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
        <RichTextContent
          path="betterbuiltintro.title"
          label="Title"
          defaultValue="<p>What &ldquo;Better-Built Living&rdquo; Means For You</p>"
          as="h2"
          className="font-heading text-3xl sm:text-4xl md:text-5xl font-normal text-[var(--vibe-color-text)] leading-tight tracking-tight"
        />
        <div className="w-16 h-1 mx-auto mt-5 rounded-full bg-[var(--vibe-color-primary)]" style={color4?.primaryOverride} />
        <RichTextContent
          path="betterbuiltintro.body"
          label="Body"
          defaultValue='<p>At <span data-type="field-token" data-field="property_name"></span>, &ldquo;Better-Built Living&rdquo; is more than just a slogan; it&rsquo;s our commitment to deliver homes that prioritize your comfort, quality, and peace of mind. Here&rsquo;s how our unique approach to off-site construction delivers a superior living experience.</p>'
          as="div"
          className="mt-6 font-body text-base md:text-lg leading-relaxed text-[var(--vibe-color-text-muted)] [&_p]:mb-0"
        />
      </div>
    </section>
  );
}

export const contentSchema: ComponentContentSchema = {
  slot: 'BetterBuiltIntro',
  displayName: 'Better-Built Intro',
  tokens: {
    'betterbuiltintro.title': {
      label: 'Title',
      type: 'richtext',
      defaultValue: '<p>What &ldquo;Better-Built Living&rdquo; Means For You</p>',
    },
    'betterbuiltintro.body': {
      label: 'Body',
      type: 'richtext',
      defaultValue:
        '<p>At <span data-type="field-token" data-field="property_name"></span>, &ldquo;Better-Built Living&rdquo; is more than just a slogan; it&rsquo;s our commitment to deliver homes that prioritize your comfort, quality, and peace of mind. Here&rsquo;s how our unique approach to off-site construction delivers a superior living experience.</p>',
    },
  },
};

(BetterBuiltIntro as unknown as { contentSchema: ComponentContentSchema }).contentSchema =
  contentSchema;

export default BetterBuiltIntro;
